/**
 * The questions on the landing page, and their answers.
 *
 * One list, read twice: Landing.jsx renders it as the page's FAQ section, and
 * scripts/prerender.mjs writes the same pairs into the page's FAQPage JSON-LD.
 * Structured data that does not match the visible page is the one thing a
 * search engine penalises about structured data, so the two cannot be allowed
 * to drift -- which is why this is a module and not two copies.
 *
 * Every answer states something the user manual states. Nothing here is a
 * promise about what the app will do; it is a description of what it does.
 * Plain sentences, no markup: the JSON-LD copy is text.
 */
export const LANDING_FAQ = [
  {
    question: 'What is Pantry Pro?',
    answer: 'Pantry Pro keeps an inventory of the food in your home. Food goes in from a '
      + 'photographed receipt, a photograph of a shelf, a barcode, or by typing it in, and the '
      + 'inventory then answers two questions: what can I cook tonight, and what do I actually '
      + 'need to buy. It ranks the recipes you have saved against what is on hand, lists what is '
      + 'running low and what should be used soon, and builds a shopping list and a weekly meal '
      + 'plan that one household shares.',
  },
  {
    question: 'Is Pantry Pro free?',
    answer: 'Yes. The inventory, barcode scanning, recipes, recipe ranking, shopping lists and meal '
      + 'plans are unlimited on every plan. The only thing that is counted is photo scans, '
      + 'because reading a photograph costs money each time: the free plan includes 5 a month, '
      + 'and the paid plan, $5 a month or $50 a year, includes 40. Nothing is ever locked when a '
      + 'subscription ends.',
  },
  {
    question: 'Where does it run?',
    answer: 'In any modern browser, on a phone or a laptop, at pantry-pro.app, and it can be '
      + 'installed to a phone’s home screen like an app. There is no password: you sign in '
      + 'with a code sent to your email address.',
  },
  {
    question: 'How does receipt scanning work?',
    answer: 'You photograph the receipt and an AI model reads it into a draft purchase, which '
      + 'opens as a review screen showing what was printed beside what it was matched to. You '
      + 'correct or exclude lines, then confirm. Nothing reaches your inventory until you '
      + 'approve it, and each confirmed receipt teaches the app that store’s abbreviations.',
  },
  {
    question: 'Does scanning a barcode use up photo scans?',
    answer: 'No. A barcode is decoded on your own device with no photograph uploaded, and the '
      + 'number is looked up against your own items, then USDA FoodData Central and Open Food '
      + 'Facts. Barcode scanning is unlimited on every plan.',
  },
  {
    question: 'What does “can’t tell” mean on a recipe?',
    answer: 'It is the app declining to guess. A recipe wants 4 cups of broth and the pantry holds '
      + '2 cans; nothing has been told how many cups a can holds, so the ingredient is marked '
      + 'can’t tell rather than have it or need. A recipe with only can’t-tells is '
      + 'labeled unclear rather than ready, and a shopping list leaves those lines off by '
      + 'default and says how many it left off.',
  },
  {
    question: 'Can my household share one pantry?',
    answer: 'Yes. A pantry is shared by design: everyone in a household sees one inventory, one '
      + 'shopping list and one meal plan, and every change is attributed by name. An owner '
      + 'invites people with a link or a short code.',
  },
  {
    question: 'Is my data used to train AI models?',
    answer: 'No. Your inventory, photographs and recipes are not used to train AI models, and your '
      + 'personal information is not sold. The privacy policy sets out exactly what is collected '
      + 'and what happens to it.',
  },
];
